import { useState, useEffect, useRef } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { feeRecords } from "@/mocks/systemModules";
import FeeRealtimeRecords from "./components/FeeRealtimeRecords";
import FeeStatsChart from "./components/FeeStatsChart";

const statusConfig: Record<string, { color: string; bg: string }> = {
  "已缴": { color: "text-green-600", bg: "bg-green-50" },
  "欠费": { color: "text-red-600", bg: "bg-red-50" },
  "部分缴": { color: "text-amber-600", bg: "bg-amber-50" },
};

const monthlyRevenue = [
  { month: "1月", revenue: 725600, arrears: 28400 },
  { month: "2月", revenue: 685200, arrears: 32100 },
  { month: "3月", revenue: 715800, arrears: 25600 },
  { month: "4月", revenue: 745300, arrears: 22800 },
  { month: "5月", revenue: 785600, arrears: 18900 },
  { month: "6月", revenue: 805200, arrears: 15600 },
  { month: "7月", revenue: 775800, arrears: 19200 },
  { month: "8月", revenue: 815300, arrears: 14800 },
  { month: "9月", revenue: 835600, arrears: 12400 },
  { month: "10月", revenue: 845200, arrears: 11200 },
  { month: "11月", revenue: 825800, arrears: 13600 },
  { month: "12月", revenue: 856234, arrears: 9800 },
];

const radarData = [
  { label: "缴费及时率", value: 92 },
  { label: "欠费回收率", value: 78 },
  { label: "用气增长率", value: 85 },
  { label: "客户满意度", value: 88 },
  { label: "营收完成率", value: 95 },
  { label: "安全合规率", value: 97 },
];

const aiReportText = `【AI营收分析报告 - 2026年4月】

一、营收总体情况
本月累计营收 ¥856,234 元，同比增长 8.3%，环比增长 3.7%，超额完成月度目标 95.1%。居民用户贡献营收占比 62.4%，工商业用户占比 37.6%。

二、欠费风险分析
当前欠费总额 ¥9,800 元，欠费用户 23 户，较上月减少 12 户。AI模型识别出 5 名高风险欠费用户，建议优先通过银行代扣方式催收，预计可回收 ¥7,200 元。

三、用气趋势预测
基于历史数据分析，预测5月份营收将达 ¥880,000-920,000 元区间，用气量预计增长 4.2%。夏季用气量通常下降，建议提前做好营收预算调整。

四、AI优化建议
1. 对欠费超过2个月的用户启动自动代扣流程
2. 向余额低于50元的用户发送充值提醒短信
3. 对用气量异常增长的工商业用户进行安检复查
4. 建议推广银行代扣业务，提升缴费便利性`;

export default function FeePage() {
  const [tab, setTab] = useState<"realtime" | "query" | "arrears" | "analysis">("realtime");
  const [search, setSearch] = useState("");
  const [payModal, setPayModal] = useState<typeof feeRecords[0] | null>(null);
  const [aiReport, setAiReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [chartType, setChartType] = useState<"bar" | "line" | "radar">("bar");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const radarRef = useRef<HTMLCanvasElement>(null);

  const filtered = feeRecords.filter(f => {
    const matchTab = tab === "arrears" ? f.status === "欠费" : true;
    const matchSearch = !search || f.userName.includes(search) || f.userId.includes(search);
    return matchTab && matchSearch;
  });

  const stats = {
    total: feeRecords.reduce((s, f) => s + f.amount, 0),
    paid: feeRecords.filter(f => f.status === "已缴").reduce((s, f) => s + f.amount, 0),
    arrears: feeRecords.filter(f => f.status === "欠费").reduce((s, f) => s + f.amount, 0),
    arrearsCount: feeRecords.filter(f => f.status === "欠费").length,
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || tab !== "analysis") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const maxV = Math.max(...monthlyRevenue.map(d => d.revenue));
    const barW = (w - 60) / monthlyRevenue.length;

    if (chartType === "bar") {
      monthlyRevenue.forEach((d, i) => {
        const x = 30 + i * barW;
        const bw = barW * 0.6;
        const barH = ((d.revenue / maxV) * (h - 50));
        const y = h - 30 - barH;

        const grad = ctx.createLinearGradient(x, y, x, h - 30);
        grad.addColorStop(0, "rgba(232,83,26,0.9)");
        grad.addColorStop(1, "rgba(232,83,26,0.2)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(x + bw * 0.1, y, bw * 0.8, barH, 3);
        ctx.fill();

        const arrH = ((d.arrears / maxV) * (h - 50));
        const arrY = h - 30 - arrH;
        ctx.fillStyle = "rgba(239,68,68,0.6)";
        ctx.beginPath();
        ctx.roundRect(x + bw * 0.1, arrY, bw * 0.8, arrH, 2);
        ctx.fill();

        ctx.fillStyle = "rgba(80,80,80,0.7)";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(d.month, x + bw / 2, h - 12);
      });

      ctx.fillStyle = "#E8531A";
      ctx.fillRect(30, 10, 12, 8);
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("营收", 46, 18);
      ctx.fillStyle = "rgba(239,68,68,0.6)";
      ctx.fillRect(90, 10, 12, 8);
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.fillText("欠费", 106, 18);
    }

    if (chartType === "line") {
      const points = monthlyRevenue.map((d, i) => ({
        x: 30 + i * barW + barW / 2,
        y: h - 30 - ((d.revenue / maxV) * (h - 50)),
      }));

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = "#E8531A";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, "rgba(232,83,26,0.2)");
      grad.addColorStop(1, "rgba(232,83,26,0)");
      ctx.beginPath();
      ctx.moveTo(points[0].x, h - 30);
      ctx.lineTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.lineTo(points[points.length - 1].x, h - 30);
      ctx.fillStyle = grad;
      ctx.fill();

      points.forEach((p, i) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = "#E8531A";
        ctx.fill();
        ctx.fillStyle = "rgba(80,80,80,0.7)";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(monthlyRevenue[i].month, p.x, h - 12);
      });

      const predX = points[points.length - 1].x + barW;
      const predY = h - 30 - ((900000 / maxV) * (h - 50));
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(points[points.length - 1].x, points[points.length - 1].y);
      ctx.lineTo(predX, predY);
      ctx.strokeStyle = "rgba(232,83,26,0.5)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#E8531A";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("AI预测", predX, predY - 8);
    }
  }, [tab, chartType]);

  useEffect(() => {
    const canvas = radarRef.current;
    if (!canvas || tab !== "analysis") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const r = Math.min(w, h) / 2 - 30;
    const n = radarData.length;

    for (let ring = 1; ring <= 5; ring++) {
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const rr = (ring / 5) * r;
        const x = cx + rr * Math.cos(angle);
        const y = cy + rr * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = "rgba(200,200,200,0.4)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + r * Math.cos(angle), cy + r * Math.sin(angle));
      ctx.strokeStyle = "rgba(200,200,200,0.4)";
      ctx.stroke();

      const lx = cx + (r + 20) * Math.cos(angle);
      const ly = cy + (r + 20) * Math.sin(angle);
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(radarData[i].label, lx, ly + 4);
    }

    ctx.beginPath();
    radarData.forEach((d, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const rr = (d.value / 100) * r;
      const x = cx + rr * Math.cos(angle);
      const y = cy + rr * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(232,83,26,0.15)";
    ctx.fill();
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.stroke();

    radarData.forEach((d, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const rr = (d.value / 100) * r;
      ctx.beginPath();
      ctx.arc(cx + rr * Math.cos(angle), cy + rr * Math.sin(angle), 4, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
    });
  }, [tab]);

  const generateReport = () => {
    setGenerating(true);
    setAiReport("");
    let i = 0;
    const timer = setInterval(() => {
      if (i >= aiReportText.length) { clearInterval(timer); setGenerating(false); return; }
      setAiReport(aiReportText.slice(0, i + 3));
      i += 3;
    }, 30);
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">费用管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">费用查询 · 欠费管理 · AI多维分析 · 智能报告</p>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
              <i className="ri-message-2-line"></i>批量催缴
            </button>
            <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>导出
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "应收总额", value: `¥${stats.total.toLocaleString()}`, color: "text-gray-800", bg: "bg-gray-50", icon: "ri-file-list-3-line" },
            { label: "已收金额", value: `¥${stats.paid.toLocaleString()}`, color: "text-green-600", bg: "bg-green-50", icon: "ri-checkbox-circle-line" },
            { label: "欠费金额", value: `¥${stats.arrears.toFixed(2)}`, color: "text-red-600", bg: "bg-red-50", icon: "ri-error-warning-line" },
            { label: "欠费用户", value: `${stats.arrearsCount}户`, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-user-unfollow-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {[
            { key: "realtime", label: "实时收费", icon: "ri-live-line" },
            { key: "query", label: "费用查询", icon: "ri-search-line" },
            { key: "arrears", label: "欠费管理", icon: "ri-error-warning-line" },
            { key: "analysis", label: "AI数据分析", icon: "ri-brain-line" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key as typeof tab)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${tab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </div>

        {tab === "realtime" && (
          <div className="space-y-4">
            <FeeStatsChart />
            <FeeRealtimeRecords />
          </div>
        )}

        {(tab === "query" || tab === "arrears") && (
          <>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索用户名/编号..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E8531A]"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["费用编号", "用户编号", "用户姓名", "费用类型", "金额", "账期", "状态", "缴费日期", "操作"].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(item => {
                    const cfg = statusConfig[item.status];
                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.userId}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.userName}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.type}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800">¥{item.amount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.period}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{item.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.payDate || "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {item.status === "欠费" && (
                              <>
                                <button onClick={() => setPayModal(item)} className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">代缴</button>
                                <button className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap">催缴</button>
                              </>
                            )}
                            {item.status === "已缴" && (
                              <button className="text-xs text-gray-400 hover:underline cursor-pointer whitespace-nowrap">查看凭证</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "analysis" && (
          <div className="space-y-4">
            {/* Chart Controls */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-brain-line text-[#E8531A]"></i>
                  <span className="text-sm font-semibold text-gray-800">AI营收多维分析</span>
                </div>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { key: "bar", label: "柱状图", icon: "ri-bar-chart-line" },
                    { key: "line", label: "曲线图", icon: "ri-line-chart-line" },
                  ].map(c => (
                    <button
                      key={c.key}
                      onClick={() => setChartType(c.key as typeof chartType)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${chartType === c.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
                    >
                      <i className={c.icon}></i>{c.label}
                    </button>
                  ))}
                </div>
              </div>
              <canvas ref={canvasRef} width={900} height={200} className="w-full" />
              <div className="mt-3 grid grid-cols-4 gap-3">
                {[
                  { label: "年度总营收", value: "¥9,416,634", trend: "+8.3%", up: true },
                  { label: "月均营收", value: "¥784,720", trend: "+5.2%", up: true },
                  { label: "年度欠费", value: "¥224,200", trend: "-18.5%", up: false },
                  { label: "AI预测下月", value: "¥900,000", trend: "+5.1%", up: true },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                    <p className="text-base font-bold text-gray-800">{item.value}</p>
                    <p className={`text-xs font-medium ${item.up ? "text-green-600" : "text-red-500"}`}>{item.trend}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Radar + AI Report */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-radar-line text-[#E8531A]"></i>
                  <span className="text-sm font-semibold text-gray-800">营收健康度雷达图</span>
                </div>
                <canvas ref={radarRef} width={320} height={260} className="w-full" />
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {radarData.map((d, i) => (
                    <div key={i} className="text-center">
                      <p className="text-sm font-bold text-[#E8531A]">{d.value}%</p>
                      <p className="text-xs text-gray-400">{d.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-robot-2-line text-[#E8531A]"></i>
                    <span className="text-sm font-semibold text-gray-800">AI智能分析报告</span>
                  </div>
                  <button
                    onClick={generateReport}
                    disabled={generating}
                    className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
                  >
                    <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-magic-line"}`}></i>
                    {generating ? "生成中..." : "生成报告"}
                  </button>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 h-64 overflow-y-auto">
                  {aiReport ? (
                    <pre className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{aiReport}{generating && <span className="animate-pulse">|</span>}</pre>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <i className="ri-robot-2-line text-gray-300 text-3xl mb-2"></i>
                      <p className="text-xs text-gray-400">点击「生成报告」按钮</p>
                      <p className="text-xs text-gray-400">AI将自动分析营收数据并生成报告</p>
                    </div>
                  )}
                </div>
                {aiReport && !generating && (
                  <button className="w-full mt-3 border border-[#E8531A] text-[#E8531A] py-2 rounded-lg text-xs cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                    <i className="ri-download-line mr-1"></i>下载PDF报告
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {payModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPayModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">代缴费用</h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">用户姓名</span>
                <span className="font-medium text-gray-800">{payModal.userName}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">费用类型</span>
                <span className="font-medium text-gray-800">{payModal.type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">应缴金额</span>
                <span className="font-bold text-[#E8531A] text-lg">¥{payModal.amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-500 mb-1 block">缴费方式</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                <option>现金</option>
                <option>微信支付</option>
                <option>支付宝</option>
                <option>银行转账</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPayModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={() => setPayModal(null)} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认代缴</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
